import { useCallback, useRef } from 'react';

interface AdEventHandlers {
  onAdStart?: () => void;
  onAdComplete?: () => void;
  onAdError?: (error: any) => void;
  onAdSkipped?: () => void;
  onAdPaused?: () => void;
  onAdResumed?: () => void;
  onAdProgress?: (adProgressData: any) => void;
  onAllAdsCompleted?: () => void;
}

export const useAdEvents = (
  playerRef: React.RefObject<any>,
  handlers: AdEventHandlers
) => {
  // Store listener references for cleanup
  const listenersRef = useRef<Array<{ event: string; listener: (event: any) => void }>>([]);

  const setupAdEventListeners = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;

    const adManager = player.getAdManager();
    if (!adManager) return;

    // Get the AdsManager instance to listen to ad events
    // Note: We need to listen to the LOADED event first to get access to the AdsManager
    const onAdsManagerLoaded = (event: any) => {
      const adsManager = event.getAdsManager();
      
      // Ad started
      const onAdStarted = () => {
        console.log('Ad started');
        handlers.onAdStart?.();
      };

      // Ad completed
      const onAdComplete = () => {
        console.log('Ad completed');
        handlers.onAdComplete?.();
      };

      // Ad error
      const onAdError = (adErrorEvent: any) => {
        console.error('Ad error:', adErrorEvent.getError());
        handlers.onAdError?.(adErrorEvent.getError());
      };

      // Ad skipped
      const onAdSkipped = () => {
        console.log('Ad skipped');
        handlers.onAdSkipped?.();
      };

      // Ad paused
      const onAdPaused = () => {
        console.log('Ad paused');
        handlers.onAdPaused?.();
      };

      // Ad resumed
      const onAdResumed = () => {
        console.log('Ad resumed');
        handlers.onAdResumed?.();
      };

      // Ad progress
      const onAdProgress = (adProgressData: any) => {
        handlers.onAdProgress?.(adProgressData);
      };

      // All ads completed
      const onAllAdsCompleted = () => {
        console.log('All ads completed');
        handlers.onAllAdsCompleted?.();
      };

      // Store listeners for cleanup
      const adEventListeners: Array<{ event: any; listener: any }> = [];

      // Register event listeners if handlers are provided
      if (window.google?.ima) {
        const AdEvent = window.google.ima.AdEvent.Type;
        const AdErrorEvent = window.google.ima.AdErrorEvent.Type;

        if (handlers.onAdStart) {
          adsManager.addEventListener(AdEvent.STARTED, onAdStarted);
          adEventListeners.push({ event: AdEvent.STARTED, listener: onAdStarted });
        }

        if (handlers.onAdComplete) {
          adsManager.addEventListener(AdEvent.COMPLETE, onAdComplete);
          adEventListeners.push({ event: AdEvent.COMPLETE, listener: onAdComplete });
        }

        if (handlers.onAdError) {
          adsManager.addEventListener(AdErrorEvent.AD_ERROR, onAdError);
          adEventListeners.push({ event: AdErrorEvent.AD_ERROR, listener: onAdError });
        }

        if (handlers.onAdSkipped) {
          adsManager.addEventListener(AdEvent.SKIPPED, onAdSkipped);
          adEventListeners.push({ event: AdEvent.SKIPPED, listener: onAdSkipped });
        }

        if (handlers.onAdPaused) {
          adsManager.addEventListener(AdEvent.PAUSED, onAdPaused);
          adEventListeners.push({ event: AdEvent.PAUSED, listener: onAdPaused });
        }

        if (handlers.onAdResumed) {
          adsManager.addEventListener(AdEvent.RESUMED, onAdResumed);
          adEventListeners.push({ event: AdEvent.RESUMED, listener: onAdResumed });
        }

        if (handlers.onAdProgress) {
          adsManager.addEventListener(AdEvent.AD_PROGRESS, onAdProgress);
          adEventListeners.push({ event: AdEvent.AD_PROGRESS, listener: onAdProgress });
        }

        if (handlers.onAllAdsCompleted) {
          adsManager.addEventListener(AdEvent.ALL_ADS_COMPLETED, onAllAdsCompleted);
          adEventListeners.push({ event: AdEvent.ALL_ADS_COMPLETED, listener: onAllAdsCompleted });
        }

        // Store the adsManager and its listeners for cleanup
        listenersRef.current.push({
          adsManager,
          listeners: adEventListeners
        } as any);
      }
    };

    // Listen for when ads are loaded
    try {
      // Shaka's ad manager uses events for tracking
      player.addEventListener('ad-started', () => {
        console.log('Shaka ad-started event');
        handlers.onAdStart?.();
      });

      player.addEventListener('ad-complete', () => {
        console.log('Shaka ad-complete event');
        handlers.onAdComplete?.();
      });

      player.addEventListener('ad-skipped', () => {
        console.log('Shaka ad-skipped event');
        handlers.onAdSkipped?.();
      });

      player.addEventListener('ad-error', (event: any) => {
        console.error('Shaka ad-error event:', event);
        handlers.onAdError?.(event);
      });

      // Store Shaka listeners
      listenersRef.current.push({
        type: 'shaka',
        player: player,
        events: ['ad-started', 'ad-complete', 'ad-skipped', 'ad-error']
      } as any);

    } catch (error) {
      console.warn('Error setting up ad event listeners:', error);
    }
  }, [playerRef, handlers]);

  const cleanupAdEventListeners = useCallback(() => {
    try {
      // Clean up all stored listeners
      listenersRef.current.forEach((listenerGroup: any) => {
        if (listenerGroup.type === 'shaka' && listenerGroup.player) {
          // Clean up Shaka player listeners
          listenerGroup.events.forEach((eventName: string) => {
            try {
              listenerGroup.player.removeEventListener(eventName, () => {});
            } catch (e) {
              console.warn(`Error removing ${eventName} listener:`, e);
            }
          });
        } else if (listenerGroup.adsManager && listenerGroup.listeners) {
          // Clean up IMA AdsManager listeners
          listenerGroup.listeners.forEach(({ event, listener }: any) => {
            try {
              listenerGroup.adsManager.removeEventListener(event, listener);
            } catch (e) {
              console.warn('Error removing ad listener:', e);
            }
          });
        }
      });

      // Clear the listeners array
      listenersRef.current = [];
    } catch (error) {
      console.warn('Error cleaning up ad event listeners:', error);
    }
  }, []);

  return {
    setupAdEventListeners,
    cleanupAdEventListeners
  };
};

